import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  signal,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import { IconComponent } from "@claas/claas-ui-components";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
  selector: "claas-horizontal-scroll-helper",
  imports: [IconComponent, TranslatePipe],
  templateUrl: "./horizontal-scroll-helper.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./horizontal-scroll-helper.scss",
})
export class HorizontalScrollHelperComponent
  implements AfterViewInit, OnDestroy
{
  /**
   * The element that should be scrolled
   */
  scrollTarget = input.required<HTMLElement>();

  /**
   * Emits when scroll position changes
   */
  scrollPositionChange = output<{
    scrolledToEnd: boolean;
    scrollLeft: number;
  }>();

  @ViewChild("scrollToStartBtn", { static: false })
  scrollToStartBtn!: ElementRef<HTMLButtonElement>;
  @ViewChild("scrollToEndBtn", { static: false })
  scrollToEndBtn!: ElementRef<HTMLButtonElement>;

  // Signal to track scroll state
  isScrolledToEnd = signal(false);
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private handleScroll = (): void => {
    this.checkScrollPosition();
  };

  ngAfterViewInit(): void {
    const targetElement = this.scrollTarget();

    if (!targetElement) {
      console.warn(
        "HorizontalScrollHelperComponent: No scroll target provided",
      );
      return;
    }

    // Initial check of scroll position with small delay to ensure DOM is ready
    setTimeout(() => {
      this.checkScrollPosition();
      this.updateScrollHelperPosition();
    }, 0);

    // Set up scroll listener on the target element
    targetElement.addEventListener("scroll", this.handleScroll);

    // Set up resize observer to handle container size changes
    this.resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to avoid excessive calls
      requestAnimationFrame(() => {
        this.checkScrollPosition();
        this.updateScrollHelperPosition();
      });
    });
    this.resizeObserver.observe(targetElement);

    // Also observe mat-sidenav-content for scrollbar changes
    const matSidenavContent = document.querySelector("mat-sidenav-content");
    if (matSidenavContent) {
      this.resizeObserver.observe(matSidenavContent);

      // Set up mutation observer to detect DOM changes that might affect scrollbar
      this.mutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          this.updateScrollHelperPosition();
        });
      });

      this.mutationObserver.observe(matSidenavContent, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"],
      });
    }
  }

  ngOnDestroy(): void {
    const targetElement = this.scrollTarget();
    if (targetElement) {
      targetElement.removeEventListener("scroll", this.handleScroll);
    }

    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  private checkScrollPosition(): void {
    const container = this.scrollTarget();
    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;
    const maxScrollLeft = scrollWidth - clientWidth;

    // If content doesn't overflow, consider it at start
    if (maxScrollLeft <= 0) {
      this.isScrolledToEnd.set(false);
      this.updateButtonVisibility(false);
      this.scrollPositionChange.emit({ scrolledToEnd: false, scrollLeft: 0 });
      return;
    }

    // Consider scrolled to end if within 5px of max scroll
    const scrolledToEnd = scrollLeft >= maxScrollLeft - 5;

    this.isScrolledToEnd.set(scrolledToEnd);
    this.updateButtonVisibility(scrolledToEnd);
    this.scrollPositionChange.emit({ scrolledToEnd, scrollLeft });
  }

  private updateButtonVisibility(scrolledToEnd: boolean): void {
    if (
      !this.scrollToStartBtn?.nativeElement ||
      !this.scrollToEndBtn?.nativeElement
    )
      return;

    if (scrolledToEnd) {
      // At end: show left button, hide right button
      this.scrollToStartBtn.nativeElement.classList.remove("hidden");
      this.scrollToEndBtn.nativeElement.classList.add("hidden");
    } else {
      // At start: show right button, hide left button
      this.scrollToStartBtn.nativeElement.classList.add("hidden");
      this.scrollToEndBtn.nativeElement.classList.remove("hidden");
    }
  }

  scrollToStart(): void {
    const container = this.scrollTarget();
    if (!container) return;

    container.scrollTo({
      left: 0,
      behavior: "smooth",
    });
  }

  scrollToEnd(): void {
    const container = this.scrollTarget();
    if (!container) return;

    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    container.scrollTo({
      left: maxScrollLeft,
      behavior: "smooth",
    });
  }

  private updateScrollHelperPosition(): void {
    // Find mat-sidenav-content element
    const matSidenavContent = document.querySelector("mat-sidenav-content");
    if (!matSidenavContent) return;

    // Check if mat-sidenav-content has a scrollbar
    const hasScrollbar =
      matSidenavContent.scrollHeight > matSidenavContent.clientHeight;

    // Get the scroll-helper element
    const scrollHelper = document.querySelector(
      ".scroll-helper",
    ) as HTMLElement;
    if (!scrollHelper) return;

    if (hasScrollbar) {
      // mat-sidenav-content has scrollbar - use fixed positioning
      scrollHelper.classList.remove("absolute-position");
      scrollHelper.classList.add("fixed-position");
    } else {
      // mat-sidenav-content has no scrollbar - position within margin area
      scrollHelper.classList.remove("fixed-position");
      scrollHelper.classList.add("absolute-position");
    }
  }
}
